import { useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Typography,
  Link,
} from "@mui/material";
import { formatDate } from "lib/util/DateTimeUtil/formatDate/formatDate";
import { AvailableSlotsListProps } from "./AvailableSlotsList.types";
import { sxStyles } from "./AvailableSlotsList.styles";
import NoSlotsMessage from "../NoSlotsMessage/NoSlotsMessage";
import SeeMoreButton from "lib/components/buttons/SeeMoreButton/SeeMoreButton";
import { AvailableSlot } from "api/appointment/GetAvailableSlots/getAvailableSlots.toUi.types";
import { renderErrorMessage } from "lib/components/error/ErrorDisplay/renderErrorMessage";
import { ACTIONS, PATHS } from "lib/routing";
import { Link as RouterLink } from "react-router";
import { mobileNavigate } from "lib/routing/navigate/navigate";
import IMAGES from "lib/assets/images";
import { useAppSelector } from "lib/redux/hooks";
import { selectNavigation } from "lib/redux/navigation/selectors";

// ==========================
// CONSTANTS
// ==========================

// No. of available slots to show when the page first loads.
const PREVIEW_COUNT = 5;

const DEFAULT_NOSLOT_MESSAGE =
  "Sorry, there are no other available appointment slots.";
// ==========================
// COMPONENT
// ==========================

const AvailableSlotsList = ({
  slots,
  isPreview,
  isDateChanged,
  hasExceedRange,
  hasMoreSlots,
  noSlotMessage,
  centerContactNo,
  showCenterContactNo,
  isLiveChatEnabled,
  onSelect,
  onNextPage,
  onShowAll,
}: AvailableSlotsListProps) => {
  const classes = sxStyles();

  // Redux States
  // current flow indicator, useful when there is different handling in the component need to apply according to the what flow it is
  const { currentFlowAction } = useAppSelector(selectNavigation);

  // Local States
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const slotsToShow = filterAvailableSlots(slots, isPreview);

  const onClickLiveChatButton = () => {
    mobileNavigate(
      ACTIONS.LIVECHAT_PAGE_INTERCEPTION(PATHS.APPOINTMENT_MOBILE.path),
    );
  };

  if (slotsToShow.length === 0) {
    return (
      <>
        <NoSlotsMessage>
          {/* use renderErrorMessage to parse noSlotMessage consider there is usecase when submit request form link is contained in the noSlotMessage */}
          {noSlotMessage
            ? renderErrorMessage(
                noSlotMessage,
                currentFlowAction || "APPOINTMENT_CREATE", // this component can be accessed by redirecting from mobile client create appointment flow hence setting of 'APPOINTMENT_CREATE' if it is falsy
                isLiveChatEnabled,
              )
            : DEFAULT_NOSLOT_MESSAGE}
        </NoSlotsMessage>

        {showCenterContactNo && centerContactNo ? (
          <Typography sx={classes.text} variant="body1_regular">
            Alternatively, please call our Contact Centre at{" "}
            {!isLiveChatEnabled ? (
              <>
                <Link href={`tel:${centerContactNo}`} sx={classes.btnLink}>
                  {centerContactNo}
                </Link>{" "}
                or{" "}
                <Link
                  component={RouterLink}
                  to={PATHS.APPOINTMENT_RESCHEDULE_FORM.path}
                  sx={classes.btnLink}
                >
                  submit a request online
                </Link>
              </>
            ) : (
              <>
                <Link href={`tel:${centerContactNo}`}>{centerContactNo}</Link>{" "}
                or{" "}
                <Link
                  component={"span"}
                  onClick={() => {
                    onClickLiveChatButton();
                  }}
                >
                  submit a request online
                </Link>
              </>
            )}
            .
          </Typography>
        ) : null}
      </>
    );
  } else {
    return (
      <Box width="100%">
        <FormControl component="fieldset" sx={classes.layout}>
          <RadioGroup
            aria-label="available-slots"
            name="available-slots"
            value={isDateChanged ? null : selectedIndex}
            onChange={(event) => {
              // index of the currently selected slot
              const indexOfSelected = parseInt(event.target.value);

              // set the state so the appropriate radio will be selected
              setSelectedIndex(indexOfSelected);

              // provide details of selected slot to the onSelect callback
              const { id, date } = slotsToShow[indexOfSelected];
              onSelect(id, date);
            }}
          >
            {slotsToShow?.map((slot, index) => {
              return (
                <FormControlLabel
                  sx={classes.listItem}
                  key={`slots${index}`}
                  value={index}
                  label={formatDate(slot.date)}
                  control={
                    <Radio
                      icon={
                        <Box
                          component={"img"}
                          src={IMAGES.general.RadioButtonUncheckedIcon}
                          sx={classes.radio}
                        />
                      }
                      checkedIcon={
                        <Box
                          component={"img"}
                          src={IMAGES.general.RadioButtonCheckedIcon}
                          sx={classes.radio}
                        />
                      }
                      sx={classes.radio}
                      color="primary"
                    />
                  }
                />
              );
            })}
          </RadioGroup>
        </FormControl>

        {/* Show More Slots Button */}
        {shouldRenderSeeMoreButton(
          slots?.length,
          isPreview,
          hasExceedRange,
          hasMoreSlots,
        ) ? (
          <SeeMoreButton
            buttonText="Show more time slots"
            onClick={() => {
              if (!isPreview) {
                // if all the slots are already shown, get next page of slots
                onNextPage();
              }
              // user has chosen to show all slots from now onwards
              onShowAll();
            }}
          />
        ) : null}
      </Box>
    );
  }
};

// ==========================
// HELPER METHODS
// ==========================

/**
 * Determines if the Show More button should be displayed or not.
 *
 * @param {number} slotCount
 *   Number of slots from API.
 * @param {boolean} isPreview
 *   Whether we are previewing only the first few slots.
 * @param {boolean} hasExceedRange
 *   Whether the slots alr exceed the min~max date range at reschedule.
 * @param {boolean | null} hasMoreSlotsFromApi
 *   Whether there are still slots to be retrieved from the remote API.
 *
 * @returns {boolean}  True if should display, false otherwise.
 */
const shouldRenderSeeMoreButton = (
  slotCount: number,
  isPreview: boolean,
  hasExceedRange: boolean,
  hasMoreSlotsFromApi: boolean | null,
) => {
  // ignoring a corner case: current API call are all slots in range, has more data, but the first slots
  // onwards are all out of range slots, ideal behaviour is hidding show more button, but now it shows
  if (isPreview && slotCount > PREVIEW_COUNT) {
    // have more than PREVIEW_COUNT slots at first load , "show more slots" will use state data
    return true;
  } else if (!hasExceedRange && hasMoreSlotsFromApi) {
    // able to fetch more useful (in-range) slots from API, "show more slots" will trigger API call
    return true;
  } else {
    // hide if no need to trigger to load more slots
    return false;
  }
};

/**
 * Only returns a list of available slots if all slots should be shown.
 * If some should not be shown, then this method returns a filtered list with
 * a reduced number of slots.
 *
 * @param {AvailableSlot[]} slots  List of available slots
 * @param {boolean} isPreview  True if previewing first few slots, False otherwise (viewing all)
 *
 * @returns {AvailableSlot[]}  Filtered list of available slots
 */
const filterAvailableSlots = (slots: AvailableSlot[], isPreview: boolean) => {
  if (slots) {
    if (!isPreview) {
      return slots;
    } else {
      return slots.filter((slot, i) => i < PREVIEW_COUNT);
    }
  } else {
    return [];
  }
};

export default AvailableSlotsList;
